import { Application, Container, Rectangle } from 'pixi.js';
import '@/utils/pixiCJK'; // CJK 禁则换行支持（副作用导入，覆写 CanvasTextMetrics 钩子）
import { CANVAS_LABEL, CANVAS_MARGIN } from '@/constants';
import { CANVAS_PC_BACKGROUND } from '@/constants/canvasConstants';
import { BookConstants } from '@/constants/bookConstants';
import { BookPage } from './book/BookPage.class';
import { Timeline } from '../timeline/Timeline.class';
import { Action, PlayAction } from '../timeline/action';
import { Audio } from './element/part';
import { getScaleWH } from '@/utils/canvasUtil';
import { isPCViewport } from '@/hooks/useViewportAccessGate';

export class Canvas {
  /** Pixi Application；init 完成前 / destroy 后为 null */
  protected app: Application | null = null;
  protected stage!: Container;

  protected backgroundContainer!: Container;
  protected elementContainer!: Container;

  protected bgmAction: PlayAction | null = null;
  protected pageMap = new Map<string, BookPage>();

  protected playActions = new Map<string, PlayAction>();

  protected timeline: Timeline | null = null;

  protected pagePaddingTime: number = 0; // 每页的padding时间

  /** 主静音状态，供子类判断是否跳过一次性音效（如手动翻页声）。 */
  protected masterMuted = false;

  constructor(container?: HTMLDivElement) {
    if (container) this.init(container);
  }

  /** 子类可关闭 Pixi ResizePlugin，改由外部提交已稳定的视口尺寸。 */
  protected shouldAutoResizeToContainer(): boolean {
    return true;
  }

  public async init(container: HTMLDivElement) {
    this.app = new Application();

    // autoDensity: true 让 PixiJS 自动将 canvas CSS 尺寸维持在容器大小，
    // 内部像素密度由 resolution 控制，避免高分屏被浏览器放大导致模糊。
    const dpr = Math.min(window.devicePixelRatio || 1, BookConstants.MAX_RENDER_RESOLUTION);
    const autoResizeToContainer = this.shouldAutoResizeToContainer();
    await this.app.init({
      ...(autoResizeToContainer
        ? { resizeTo: container }
        : {
            width: Math.max(1, container.clientWidth),
            height: Math.max(1, container.clientHeight)
          }),
      background: CANVAS_PC_BACKGROUND,
      autoDensity: true,
      antialias: true,
      resolution: dpr
    });

    // HMR 热更新可能在 await 期间触发 destroy() 将 this.app 置为 null，
    // 此时继续初始化会访问 null 引用导致崩溃，直接中止
    if (!this.app) return;

    container.append(this.app.canvas);

    // 步骤 1：初始化后按当前视口同步画布背景色
    this.syncCanvasBackground();

    // 调用dev 拓展时用,平时不使用
    // globalThis.__PIXI_APP__ = this.app;

    // window.__PIXI_DEVTOOLS__ = {
    //   app: this.app
    // };

    this.stage = this.app.stage;

    this.updateCenter();
    this.initLayers();
    this.initOthers();
    this.initEvents();
  }

  private initLayers() {
    if (!this.app || !this.stage) return;

    this.backgroundContainer = new Container({ label: CANVAS_LABEL.BACKGROUND_CONTAINER });
    this.elementContainer = new Container({ label: CANVAS_LABEL.ELEMENT_CONTAINER });

    this.stage.addChild(this.backgroundContainer);
    this.stage.addChild(this.elementContainer);
  }

  /**
   * 子类 EditorCanvas 重写此方法以注入拖拽、辅助线、变换器等编辑器交互逻辑。
   * 基类仅绑定 resize 并开启舞台事件。
   */
  protected initEvents() {
    if (!this.app || !this.stage) return;

    this.app.renderer.on('resize', this.resize);
    window.addEventListener('resize', this.resize);
    this.stage.eventMode = 'static';
  }

  /**
   * 子类 EditorCanvas 重写此方法以初始化编辑器特有属性
   * （currentPage、shadowFilter 等）。基类为空实现。
   */
  protected initOthers() {}

  public initTimeline() {
    // 清除旧 timeline 并重建，确保每次调用都是干净的状态
    if (!this.timeline) {
      this.timeline = new Timeline();
      this.timeline.init();
    } else {
      this.timeline.reset();
    }

    // 按 pageIndex 排序
    const sortedPages = Array.from(this.pageMap.values()).sort((a, b) => a.pageIndex - b.pageIndex);

    // 根据 duration 顺序计算 startTime，然后插入 timeline
    let accumulatedTime = 0;
    sortedPages.forEach((item) => {
      // 更新 data.startTime
      if (item.data) {
        item.data.startTime = accumulatedTime;
      }

      item.initTween();

      if (item.tween) {
        this.timeline?.addTween(item.tween, item.data?.startTime ?? 0, true);
      }

      accumulatedTime += item.duration + this.pagePaddingTime;
    });

    if (this.bgmAction) {
      this.bgmAction.duration = accumulatedTime;
      this.bgmAction.init();
      this.timeline?.addTween(this.bgmAction.tween!, 0, true);
      // accumulatedTime = Math.min(accumulatedTime, this.bgmAction.duration);
    }

    // 更新 timeline 总时长
    if (accumulatedTime > 0) {
      this.timeline.duration = accumulatedTime;
    }

    // 批量添加完成后统一刷新一次，避免 addTween 逐条渲染的开销
    this.timeline?.forceSeek(0);

    // 时间轴重建后同步可播放的音频 Action，供 play()/pause() 驱动音效。
    this.updatePlayActions();
  }

  /**
   * 更新播放Action的列表
   */
  public updatePlayActions() {
    this.playActions.clear();

    this.pageMap.forEach((page) => {
      page.actions.forEach((action) => {
        if (action instanceof PlayAction) this.playActions.set(action.id, action);
      });
    });

    if (this.bgmAction) this.playActions.set(this.bgmAction.id, this.bgmAction);
  }

  public addBgm(action: PlayAction) {
    if (this.bgmAction) {
      if (this.bgmAction.tween) this.timeline?.removeTween(this.bgmAction.tween);
      this.bgmAction.destroy();
    }

    this.bgmAction = action;
    if (action.tween) this.timeline?.addTween(action.tween, 0);
  }

  /** 移除当前 BGM（如果有） */
  public removeBgm() {
    if (this.bgmAction) {
      if (this.bgmAction.tween) this.timeline?.removeTween(this.bgmAction.tween);
      this.bgmAction.destroy();
      this.bgmAction = null;
    }
  }

  /**
   * 新增页：创建并加入画布（默认隐藏，仅当前页可见）
   */
  public addPage(page: BookPage) {
    if (this.pageMap.has(page.pageId)) return;

    this.pageMap.set(page.pageId, page);

    if (page.tween) this.timeline?.addTween(page.tween, page.data?.startTime ?? 0);
  }

  /**
   * 删除页：销毁并从画布移除
   */
  public removePage(id: string) {
    const page = this.pageMap.get(id);
    if (!page) return;

    page.actions.forEach((action) => {
      if (action instanceof PlayAction) this.playActions.delete(action.id);
    });

    if (page.tween) this.timeline?.removeTween(page.tween);

    page.destroy({ children: true });
    this.pageMap.delete(id);
  }

  /**
   * 更新页面之间的间隔时间，翻页时长更新时调用
   * @param time
   */
  public updatePagePaddingTime(time: number) {
    this.pagePaddingTime = time;
  }

  public setPageWatermark(flag: boolean) {
    if (!this.pageMap) return;

    this.pageMap.forEach((page) => {
      page.setWatermark(flag);
    });
  }

  public hidePageWatermark() {
    if (!this.pageMap) return;
    this.pageMap.forEach((page) => {
      page.hideWatermark();
    });
  }

  /**
   * 获取当前已渲染的页 id 列表（供外部做差量同步）
   */
  public getPageIds(): string[] {
    return Array.from(this.pageMap.keys());
  }

  /**
   * 按 id 取已渲染的页实例（供外部维护 currentPage 引用）
   */
  public getPage(id: string): BookPage | null {
    return this.pageMap.get(id) ?? null;
  }

  /**
   * 在所有页中按 action id 查找 Action 实例
   */
  public getActionById(id: string): Action | null {
    for (const page of this.pageMap.values()) {
      const action = page.actions.get(id);
      if (action) return action;
    }
    return null;
  }

  /**
   * 获取所有页实例（按插入顺序，即页面顺序），供导出等场景遍历
   */
  public getPages(): BookPage[] {
    return Array.from(this.pageMap.values());
  }

  public seek(time: number) {
    this.timeline?.seek(time);
  }

  public play() {
    this.playActions.forEach((action) => action.play());

    this.timeline?.play();
  }

  public pause() {
    this.playActions.forEach((action) => action.pause());

    this.timeline?.pause();
  }

  /** 设置主音量（0~1），作用于全部书本音频（BGM、页面音效、翻页声）。 */
  public setMasterVolume(volume: number) {
    Audio.setMasterVolume(volume);
  }

  /** 设置主静音，作用于全部书本音频。 */
  public setMasterMuted(muted: boolean) {
    this.masterMuted = muted;
    Audio.setMasterMuted(muted);
  }

  private updateCenter() {
    //舞台居中，把(0,0)点设为屏幕中点
    if (!this.app || !this.stage) return;

    const w = this.app.screen.width;
    const h = this.app.screen.height;

    this.stage.x = w / 2;
    this.stage.y = h / 2;

    this.stage.hitArea = new Rectangle(-w / 2, -h / 2, w, h);
  }

  /**
   * 将指定 BookPage 等比缩放至画布可见区域内（四边至少保留 minMargin 间距）。
   * 复用 canvasUtil.getScaleWH 计算适配尺寸后换算为 scale。
   */
  public fitPageToCanvas(page: BookPage): void {
    if (!this.app) return;

    const minMargin = CANVAS_MARGIN;
    const regionW = this.app.screen.width - minMargin * 2;
    const regionH = this.app.screen.height - minMargin * 2;
    const pageRatio = page.pageWidth / page.pageHeight;

    const { width: fittedW } = getScaleWH(regionW, regionH, pageRatio);
    const scale = fittedW / page.pageWidth;

    page.updatePageScale(scale);
  }

  /** resize 时子类可重写的钩子，用于同步刷新当前页缩放等。 */
  protected onCanvasResize(): void {}

  /**
   * 是否开启画布的事件监听
   * @param flag
   */
  public updateListening(flag: boolean) {
    if (!this.stage) return;

    this.stage.eventMode = flag ? 'static' : 'none';
  }

  /** 判断是否为移动端视口（复用 isPCViewport 同一套规则） */
  protected isMobileViewport(): boolean {
    return !isPCViewport();
  }

  /**
   * 同步 Pixi 画布背景色：
   * #F8FAFC
   * 在 init、resize 及视口切换时调用。
   * 需在 app.init 完成且 renderer 就绪后才可写背景，避免预览弹窗尺寸 watcher 抢跑。
   */
  public syncCanvasBackground(): void {
    // 步骤 1：init 进行中或已 destroy 时 renderer/background 可能尚未就绪
    const background = this.app?.renderer?.background;
    if (!background) return;

    // 步骤 2：按当前视口写入背景色
    background.color = CANVAS_PC_BACKGROUND;
  }

  public resize = () => {
    // init 未完成或已销毁时跳过，避免访问空 renderer
    if (!this.app?.renderer) return;

    // 步骤 1：视口变化时先同步背景色
    this.syncCanvasBackground();
    // 步骤 2：更新舞台居中与命中区域
    this.updateCenter();
    // 步骤 3：通知子类处理尺寸变化
    this.onCanvasResize();
  };

  protected removeEvents() {
    this.app?.renderer?.off('resize', this.resize);
    window.removeEventListener('resize', this.resize);
  }

  public clearTimeline() {
    this.timeline?.destroy();
    this.timeline = null;
  }

  public clearElements() {
    const pageToLink = Array.from(this.pageMap.values());

    // 2. 遍历数组，安全地安全移除（此时无论内部怎么删 map，都不会影响循环）
    pageToLink.forEach((page) => {
      this.removePage(page.data?.id ?? '');
    });
    this.pageMap.clear();

    this.playActions.clear();

    // 安全销毁容器中残留的子元素，避免孤儿节点泄漏
    this.backgroundContainer
      ?.removeChildren()
      .forEach((child) => child.destroy({ children: true }));
    this.elementContainer?.removeChildren().forEach((child) => child.destroy({ children: true }));
  }

  public destroy() {
    // 1. 清理 bgm（Canvas 直接持有，不属于任何页面）
    if (this.bgmAction) {
      if (this.bgmAction.tween) this.timeline?.removeTween(this.bgmAction.tween);
      this.bgmAction.destroy();
      this.bgmAction = null;
    }

    // 2. 清理所有页面
    this.clearElements();

    // 3. 清理时间轴（GSAP）
    this.clearTimeline();

    // 4. 移除渲染器事件监听
    this.removeEvents();

    // 5. 销毁 PixiJS Application：
    //    - removeView: 从 DOM 移除 canvas
    //    - children/texture/textureSource: 递归销毁 stage 子树中所有显示对象及 GPU 资源
    //
    //    注意：不使用 releaseGlobalResources。
    //    页面上可能同时存在多个 PixiJS Application（如编辑器 + 预览弹窗），
    //    若任一个先销毁并释放全局纹理池，另一个的 Text.destroy() → CanvasTextSystem.returnTexture()
    //    就会在已清空的池上执行 push 而崩溃。
    //
    //    注意：只有在 init() 完成后（stage 就绪）才安全调用 app.destroy()。
    //    如果在 init() 异步过程中（如 HMR 热更新打断），ResizePlugin 内部
    //    _cancelResize 尚未就绪，调用 destroy() 会抛出 TypeError。
    if (this.stage) {
      this.app?.destroy(
        { removeView: true },
        { children: true, texture: true, textureSource: true }
      );
    }
    // 6. 置空引用，避免 destroy 后 watcher/resize 仍把 app 当可用实例
    this.app = null;
  }

  public getCanvas() {
    return this.app?.canvas ?? null;
  }

  public getApp() {
    return this.app;
  }

  public getTimeline() {
    return this.timeline;
  }
}
